import { GRID_CONFIG, sortByAuditUpdatedOn } from "../config";
import axios from "axios";
import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import {
    FaSearch,
    FaTimes,
    FaAngleDoubleLeft,
    FaAngleLeft,
    FaAngleRight,
    FaAngleDoubleRight,
    FaEdit,
    FaPlus,
} from "react-icons/fa";
import "react-toastify/dist/ReactToastify.css";

import { notifyUser } from "../utils/notifyUser";

const AdminSEOList = () => {
    const [seoPages, setSeoPages] = useState([]);
    const [search, setSearch] = useState("");
    const [perPage, setPerPage] = useState(GRID_CONFIG.defaultPageSize);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [selectedSEO, setSelectedSEO] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [formData, setFormData] = useState({
        slug: "",
        title: "",
        meta_description: "",
        seo_terms: [],
    });

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: "var(--navy)",
                color: "white",
                fontWeight: "bold",
                fontSize: "16px",
                minHeight: "50px",
            },
        },
        rows: {
            style: {
                minHeight: "48px",
                "&:hover": {
                    backgroundColor: "#f2f9ff",
                },
            },
        },
    };

    const columns = [
        {
            name: "Slug",
            selector: (row) => row.slug,
            sortable: true,
            width: "15%",
            cell: (row) => (
                <span style={{ fontWeight: "500", color: "var(--navy)" }}>
                    {row.slug}
                </span>
            ),
        },
        {
            name: "Title",
            selector: (row) => row.title || "Not set",
            sortable: true,
            width: "25%",
            cell: (row) => (
                <span>
                    {row.title
                        ? row.title.length > 40
                            ? `${row.title.substring(0, 40)}...`
                            : row.title
                        : "Not set"}
                </span>
            ),
        },
        {
            name: "Meta Description",
            selector: (row) => row.meta_description || "Not set",
            width: "25%",
            cell: (row) => (
                <span>
                    {row.meta_description
                        ? row.meta_description.length > 50
                            ? `${row.meta_description.substring(0, 50)}...`
                            : row.meta_description
                        : "Not set"}
                </span>
            ),
        },
        {
            name: "SEO Terms",
            selector: (row) => row.seo_terms || [],
            width: "15%",
            cell: (row) => {
                const terms = Array.isArray(row.seo_terms) ? row.seo_terms : [];
                return (
                    <span>
                        {terms.length > 0
                            ? terms.length === 1
                                ? terms[0]
                                : `${terms[0]} +${terms.length - 1}`
                            : "None"}
                    </span>
                );
            },
        },
        {
            name: "Last Updated",
            selector: (row) => new Date(row.audit_updated_on).toLocaleString(),
            sortable: true,
            width: "13%",
            cell: (row) => (
                <span>{new Date(row.audit_updated_on).toLocaleString()}</span>
            ),
        },
        {
            name: "Actions",
            width: "7%",
            cell: (row) => (
                <button
                    onClick={() => handleEdit(row)}
                    style={{
                        backgroundColor: "transparent",
                        color: "var(--navy)",
                        border: "none",
                        borderRadius: "4px",
                        padding: "6px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "16px",
                        transition: "all 0.2s ease",
                    }}
                    onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = "#f2f9ff";
                        e.currentTarget.style.color = "#003658";
                    }}
                    onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                        e.currentTarget.style.color = "var(--navy)";
                    }}
                    title="Edit SEO"
                >
                    <FaEdit size={14} />
                </button>
            ),
        },
    ];

    useEffect(() => {
        fetchSEOPages();
    }, []);

    const fetchSEOPages = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/admin/seo`,
                { withCredentials: true },
            );
            if(response.data.success) {
                setSeoPages(sortByAuditUpdatedOn(response.data.data));
            }
        } catch(error) {
            console.error("Error fetching SEO pages:", error);
            notifyUser(null, "error", "Failed to load SEO pages", "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setLoading(false);
        }
    };

    const filteredPages = seoPages.filter((page) => {
        const searchLower = search.toLowerCase();
        return (
            page.slug.toLowerCase().includes(searchLower) ||
            (page.title && page.title.toLowerCase().includes(searchLower)) ||
            (page.meta_description &&
                page.meta_description.toLowerCase().includes(searchLower))
        );
    });

    const handleExportSEOPagesCSV = () => {
        try {
            const filtered = seoPages.filter((page) => {
                const searchLower = search.toLowerCase();
                return (
                    page.slug.toLowerCase().includes(searchLower) ||
                    (page.title && page.title.toLowerCase().includes(searchLower)) ||
                    (page.meta_description &&
                        page.meta_description.toLowerCase().includes(searchLower))
                );
            });

            if (!filtered || filtered.length === 0) {
                notifyUser(null, "info", "No SEO pages to export", "toast", null, null, null, null, null, false, false, "top-center");
                return;
            }

            const rows = filtered.map((p) => ({
                Slug: p.slug || "",
                Title: p.title || "",
                MetaDescription: p.meta_description || "",
                SEOTerms: Array.isArray(p.seo_terms) ? p.seo_terms.join(", ") : p.seo_terms || "",
                LastUpdated: p.audit_updated_on
                    ? new Date(p.audit_updated_on).toLocaleString()
                    : "",
            }));

            const header = Object.keys(rows[0]).join(",");
            const csvBody = rows
                .map((r) =>
                    Object.values(r)
                        .map((v) => `"${String(v).replace(/\"/g, '""')}"`)
                        .join(","),
                )
                .join("\n");

            const csv = `${header}\n${csvBody}`;
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
                "download",
                `seo-pages-${new Date().toISOString().slice(0, 10)}.csv`,
            );
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            notifyUser(null, "error", "Failed to export SEO pages", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    const totalPages = Math.ceil(filteredPages.length / perPage);
    const paginatedData = filteredPages.slice(
        (currentPage - 1) * perPage,
        currentPage * perPage,
    );

    const fromRecord = (currentPage - 1) * perPage + 1;
    const toRecord = Math.min(currentPage * perPage, filteredPages.length);
    const totalRecords = filteredPages.length;

    const CustomPagination = () => (
        <div className="flex justify-between items-center px-2 py-3 text-sm text-gray-700 mt-4">
            <span>
                Showing {totalRecords === 0 ? 0 : fromRecord} to {toRecord} of{" "}
                {totalRecords} records
            </span>
            <div className="flex gap-2 text-lg items-center">
                <button
                    disabled={currentPage === 1 || loading}
                    onClick={() => setCurrentPage(1)}
                    className="disabled:opacity-40"
                >
                    <FaAngleDoubleLeft />
                </button>
                <button
                    disabled={currentPage === 1 || loading}
                    onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    className="disabled:opacity-40"
                >
                    <FaAngleLeft />
                </button>
                <span className="text-sm px-2">
                    Page {currentPage} of {totalPages}
                </span>
                <button
                    disabled={currentPage === totalPages || loading}
                    onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    className="disabled:opacity-40"
                >
                    <FaAngleRight />
                </button>
                <button
                    disabled={currentPage === totalPages || loading}
                    onClick={() => setCurrentPage(totalPages)}
                    className="disabled:opacity-40"
                >
                    <FaAngleDoubleRight />
                </button>
            </div>
        </div>
    );

    const handleEdit = (seo) => {
        setSelectedSEO(seo);
        setFormData({
            slug: seo.slug,
            title: seo.title || "",
            meta_description: seo.meta_description || "",
            seo_terms: Array.isArray(seo.seo_terms) ? seo.seo_terms : [],
        });
        setIsEditModalOpen(true);
    };

    const handleCreate = () => {
        setSelectedSEO(null);
        setFormData({
            slug: "",
            title: "",
            meta_description: "",
            seo_terms: [],
        });
        setIsCreateModalOpen(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleTermsChange = (e) => {
        const termsString = e.target.value;
        setFormData((prev) => ({
            ...prev,
            seo_terms: termsString,
        }));
    };

    const handleTermsBlur = () => {
        // Parse terms when user finishes editing
        const termsString = formData.seo_terms;
        if(typeof termsString === "string") {
            const termsArray = termsString
                .split(",")
                .map((term) => term.trim())
                .filter((term) => term.length > 0);
            setFormData((prev) => ({
                ...prev,
                seo_terms: termsArray,
            }));
        }
    };

    const handleSave = async () => {
        try {
            // Validation
            if(!formData.slug.trim()) {
                notifyUser(null, "error", "Slug is required", "toast", null, null, null, null, null, false, false, "top-center");
                return;
            }

            const payload = {
                slug: formData.slug.trim(),
                title: formData.title.trim(),
                meta_description: formData.meta_description.trim(),
                seo_terms: formData.seo_terms,
            };

            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/admin/seo`,
                payload,
                { withCredentials: true },
            );

            if(response.data.success) {
                notifyUser(null, "success", selectedSEO
                        ? "SEO data updated successfully"
                        : "SEO data created successfully", "toast", null, null, null, null, null, false, false, "top-center");
                setIsEditModalOpen(false);
                setIsCreateModalOpen(false);
                fetchSEOPages();
            }
        } catch(error) {
            console.error("Error saving SEO data:", error);
            notifyUser(null, "error", error.response?.data?.error || "Failed to save SEO data", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    const closeModal = () => {
        setIsEditModalOpen(false);
        setIsCreateModalOpen(false);
        setSelectedSEO(null);
    };

    return (
        <div
            className="aqi-grid-page"
            style={{
                padding: "30px",
                width: "90%",
                margin: "30px auto",
                boxShadow: "0 0 20px rgba(0,0,0,0.1)",
                borderRadius: "8px",
                backgroundColor: "white",
                position: "relative",
            }}
        >
            <div style={{ marginBottom: "20px" }}>
                <h2
                    style={{
                        color: "var(--navy)",
                        margin: "0",
                        fontSize: "large",
                        fontFamily: "large",
                    }}
                >
                    SEO MANAGEMENT
                </h2>
            </div>

            <div className="aqi-grid-toolbar">
                <div>
                        <select
                            style={{
                                padding: "0.375rem 2rem 0.375rem 0.75rem",
                                color: "var(--navy)",
                                backgroundColor: "#F5F7FA",
                                border: "2px solid #C2D7E2",
                                borderRadius: "0.375rem",
                                fontWeight: "500",
                                appearance: "none",
                                backgroundImage:
                                    "var(--navy-select-chevron)",
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "right 0.5rem center",
                                backgroundSize: "16px 12px",
                                transition: "all 0.2s ease",
                                cursor: "pointer",
                                outline: "none",
                            }}
                            className="aqi-grid-control hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                            value={perPage}
                            onChange={(e) => {
                                setPerPage(Number(e.target.value));
                                setCurrentPage(1);
                            }}
                        >
                            {GRID_CONFIG.pageSizeOptions.map((option) => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                    </div>
                <div className="aqi-grid-search">
                        <FaSearch
                            className="absolute left-3 top-2.5"
                            style={{ color: "var(--navy)" }}
                        />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search SEO pages..."
                            style={{
                                paddingLeft: "2.5rem",
                                paddingRight: "0.75rem",
                                paddingTop: "0.5rem",
                                paddingBottom: "0.5rem",
                                borderRadius: "0.375rem",
                                border: "2px solid #C2D7E2",
                                backgroundColor: "#F5F7FA",
                                transition: "all 0.2s ease",
                                outline: "none",
                                color: "var(--navy)",
                            }}
                            className="aqi-grid-search-input hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                        />
                </div>
                <div className="aqi-grid-toolbar-actions aqi-grid-toolbar-actions--export-last">
                    <button
                        onClick={handleCreate}
                        className="aqi-grid-button aqi-grid-button--primary"
                    >
                        <FaPlus size={14} />
                        Add SEO Page
                    </button>
                    <button
                        onClick={handleExportSEOPagesCSV}
                        title="Export CSV"
                        className="aqi-grid-button aqi-grid-button--primary"
                    >
                        Export
                    </button>
                </div>
            </div>

            <DataTable
                columns={columns}
                data={paginatedData}
                customStyles={customStyles}
                progressPending={loading}
                pagination={false}
                striped
                responsive
                highlightOnHover
            />
            <CustomPagination />

            {/* Edit/Create Modal */}
            {(isEditModalOpen || isCreateModalOpen) && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0,0,0,0.6)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1000,
                    }}
                >
                    <div
                        style={{
                            backgroundColor: "white",
                            padding: "25px",
                            borderRadius: "12px",
                            width: "90%",
                            maxWidth: "600px",
                            maxHeight: "90vh",
                            overflow: "auto",
                            position: "relative",
                            boxShadow: "0 0 30px rgba(0,0,0,0.3)",
                            border: "2px solid #C2D7E2",
                        }}
                    >
                        <button
                            onClick={closeModal}
                            style={{
                                position: "absolute",
                                top: "15px",
                                right: "15px",
                                background: "var(--navy)",
                                border: "none",
                                borderRadius: "50%",
                                width: "30px",
                                height: "30px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                color: "white",
                                transition: "all 0.2s ease",
                            }}
                            onMouseOver={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                    "#003658")
                            }
                            onMouseOut={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                    "var(--navy)")
                            }
                        >
                            <FaTimes />
                        </button>

                        <h2
                            style={{
                                color: "var(--navy)",
                                marginBottom: "20px",
                                borderBottom: "2px solid #C2D7E2",
                                paddingBottom: "15px",
                                fontSize: "24px",
                                fontWeight: "600",
                            }}
                        >
                            {isEditModalOpen ? "Edit SEO Page" : "Add SEO Page"}
                        </h2>

                        <div style={{ marginBottom: "15px" }}>
                            <label
                                style={{
                                    display: "block",
                                    marginBottom: "5px",
                                    color: "var(--navy)",
                                    fontWeight: "500",
                                }}
                            >
                                Page Slug *
                            </label>
                            <input
                                type="text"
                                name="slug"
                                value={formData.slug}
                                onChange={handleInputChange}
                                placeholder="/page-url"
                                style={{
                                    width: "100%",
                                    padding: "10px",
                                    border: "2px solid #C2D7E2",
                                    borderRadius: "6px",
                                    fontSize: "14px",
                                    backgroundColor: "#F5F7FA",
                                    transition: "all 0.2s ease",
                                    outline: "none",
                                }}
                                className="hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                                disabled={isEditModalOpen}
                            />
                            <small style={{ color: "#666", fontSize: "12px" }}>
                                {isEditModalOpen
                                    ? "Slug cannot be changed for existing pages"
                                    : "Enter the page URL path (e.g., /about, /contact)"}
                            </small>
                        </div>

                        <div style={{ marginBottom: "15px" }}>
                            <label
                                style={{
                                    display: "block",
                                    marginBottom: "5px",
                                    color: "var(--navy)",
                                    fontWeight: "500",
                                }}
                            >
                                Page Title
                            </label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                placeholder="Page title for SEO"
                                style={{
                                    width: "100%",
                                    padding: "10px",
                                    border: "2px solid #C2D7E2",
                                    borderRadius: "6px",
                                    fontSize: "14px",
                                    backgroundColor: "#F5F7FA",
                                    transition: "all 0.2s ease",
                                    outline: "none",
                                }}
                                className="hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                            />
                            <small style={{ color: "#666", fontSize: "12px" }}>
                                Title that appears in search results and browser
                                tabs
                            </small>
                        </div>

                        <div style={{ marginBottom: "15px" }}>
                            <label
                                style={{
                                    display: "block",
                                    marginBottom: "5px",
                                    color: "var(--navy)",
                                    fontWeight: "500",
                                }}
                            >
                                Meta Description
                            </label>
                            <textarea
                                name="meta_description"
                                value={formData.meta_description}
                                onChange={handleInputChange}
                                placeholder="Brief description for search engines"
                                rows={3}
                                style={{
                                    width: "100%",
                                    padding: "10px",
                                    border: "2px solid #C2D7E2",
                                    borderRadius: "6px",
                                    fontSize: "14px",
                                    backgroundColor: "#F5F7FA",
                                    transition: "all 0.2s ease",
                                    outline: "none",
                                    resize: "vertical",
                                }}
                                className="hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                            />
                            <small style={{ color: "#666", fontSize: "12px" }}>
                                Description that appears under the title in
                                search results (150-160 characters recommended)
                            </small>
                        </div>

                        <div style={{ marginBottom: "15px" }}>
                            <label
                                style={{
                                    display: "block",
                                    marginBottom: "5px",
                                    color: "var(--navy)",
                                    fontWeight: "500",
                                }}
                            >
                                SEO Keywords/Terms
                            </label>
                            <input
                                type="text"
                                value={
                                    Array.isArray(formData.seo_terms)
                                        ? formData.seo_terms.join(", ")
                                        : formData.seo_terms
                                }
                                onChange={handleTermsChange}
                                onBlur={handleTermsBlur}
                                placeholder="keyword1, keyword2, keyword3"
                                style={{
                                    width: "100%",
                                    padding: "10px",
                                    border: "2px solid #C2D7E2",
                                    borderRadius: "6px",
                                    fontSize: "14px",
                                    backgroundColor: "#F5F7FA",
                                    transition: "all 0.2s ease",
                                    outline: "none",
                                }}
                                className="hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                            />
                            <small style={{ color: "#666", fontSize: "12px" }}>
                                Comma-separated keywords for search optimization
                            </small>
                        </div>

                        <div
                            style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: "15px",
                                paddingTop: "15px",
                                borderTop: "1px solid #C2D7E2",
                            }}
                        >
                            <button
                                onClick={closeModal}
                                style={{
                                    backgroundColor: "#6b7280",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "6px",
                                    padding: "10px 20px",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    transition: "all 0.2s ease",
                                }}
                                onMouseOver={(e) =>
                                    (e.currentTarget.style.backgroundColor =
                                        "#4b5563")
                                }
                                onMouseOut={(e) =>
                                    (e.currentTarget.style.backgroundColor =
                                        "#6b7280")
                                }
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                style={{
                                    backgroundColor: "var(--navy)",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "6px",
                                    padding: "10px 20px",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    transition: "all 0.2s ease",
                                }}
                                onMouseOver={(e) =>
                                    (e.currentTarget.style.backgroundColor =
                                        "#003658")
                                }
                                onMouseOut={(e) =>
                                    (e.currentTarget.style.backgroundColor =
                                        "var(--navy)")
                                }
                            >
                                {isEditModalOpen ? "Update SEO" : "Create SEO"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>

    );
};

export default AdminSEOList;
